import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styles from "./ReplyPage.module.css";
import { apiUrl, getApiBaseUrl } from "../config/api";

type ValidateReplyTokenResponse = {
  ok: boolean;
  error?: string | null;

  interestId?: number;
  listingId?: number | null;
  listingSummary?: string | null;
  expiresUtc?: string | null;

  direction?: "OwnerToInterested" | "InterestedToOwner" | null;
};

type MsgPreview = {
  from: "You" | "Them";
  message: string;
  createdUtc: string; // ISO
};

type ReplyMessagesResponse = {
  ok: boolean;
  error?: string | null;
  messages?: MsgPreview[];
};

type UiState = "loading" | "invalid" | "ready" | "sending" | "sent";

function getTokenFromLocation(search: string): string {
  return new URLSearchParams(search).get("t") ?? "";
}

function formatExpiry(expiresUtc?: string | null): string | null {
  if (!expiresUtc) return null;
  const d = new Date(expiresUtc);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function parseRetryAfterSeconds(resp: Response): number | null {
  const h = resp.headers.get("Retry-After");
  if (!h) return null;
  const n = Number(h);
  if (!Number.isNaN(n) && n > 0) return Math.ceil(n);
  return null;
}

export default function ReplyPage() {
  const { search } = useLocation();
  const token = useMemo(() => getTokenFromLocation(search), [search]);
  const replyUrl = useMemo(() => window.location.href, []);

  const [ui, setUi] = useState<UiState>("loading");
  const [ctx, setCtx] = useState<ValidateReplyTokenResponse | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  // ✅ Recent messages preview
  const [recent, setRecent] = useState<MsgPreview[]>([]);

  // Copy feedback
  const [copyOk, setCopyOk] = useState<string | null>(null);

  // Abuse throttling (countdown)
  const [cooldownSec, setCooldownSec] = useState<number>(0);

  // For animation class toggles
  const [mounted, setMounted] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const focusedOnceRef = useRef(false);

  const remainingChars = 2000 - message.length;
  const expiryText = formatExpiry(ctx?.expiresUtc);

  const replyingToText =
    ctx?.direction === "OwnerToInterested"
      ? "the interested swapper"
      : ctx?.direction === "InterestedToOwner"
        ? "the listing owner"
        : null;

  // initial mount animation
  useEffect(() => {
    const t = window.setTimeout(() => setMounted(true), 10);
    return () => window.clearTimeout(t);
  }, []);

  // countdown ticker
  useEffect(() => {
    if (cooldownSec <= 0) return;
    const t = window.setInterval(() => {
      setCooldownSec((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [cooldownSec]);

  // Validate token on load + load message preview
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setError(null);
      setCopyOk(null);
      setRecent([]); // reset preview when token changes

      if (!token) {
        setUi("invalid");
        setError("Missing reply token.");
        return;
      }

      setUi("loading");

      const validateUrl = apiUrl(`/api/reply/validate?t=${encodeURIComponent(token)}`);

      // ✅ Debug: confirms env base URL + final validate URL
      console.log("API_BASE =", getApiBaseUrl());
      console.log("VALIDATE_URL =", validateUrl);

      try {
        const resp = await fetch(validateUrl, {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        if (resp.status === 429) {
          const retry = parseRetryAfterSeconds(resp) ?? 30;
          if (!cancelled) {
            setCooldownSec(retry);
            setUi("invalid");
            setError(`Too many requests. Try again in ${retry}s.`);
          }
          return;
        }

        const data = (await resp.json()) as ValidateReplyTokenResponse;

        if (cancelled) return;

        setCtx(data);

        if (!resp.ok || !data.ok) {
          setUi("invalid");
          setError(data.error ?? "This link is invalid or has expired.");
          return;
        }

        setUi("ready");

        // ✅ Load last few messages (optional enhancement)
        try {
          const msgUrl = apiUrl(`/api/reply/messages?t=${encodeURIComponent(token)}&take=3`);
          console.log("MESSAGES_URL =", msgUrl);

          const msgResp = await fetch(msgUrl, {
            method: "GET",
            headers: { Accept: "application/json" },
          });

          if (!msgResp.ok) return;

          const msgData = (await msgResp.json()) as ReplyMessagesResponse;

          if (cancelled) return;

          if (msgData?.ok && Array.isArray(msgData.messages)) {
            // Ensure shape is what we expect
            setRecent(
              msgData.messages.filter(
                (m) =>
                  (m.from === "You" || m.from === "Them") &&
                  typeof m.message === "string" &&
                  typeof m.createdUtc === "string"
              )
            );
          }
        } catch (e) {
          // swallow preview failures - reply still works fine
          console.warn("Message preview load failed:", e);
        }
      } catch (e: any) {
        console.error("Validate failed:", e);
        if (!cancelled) {
          setUi("invalid");
          setError("Could not validate this link. Please try again.");
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Auto-focus once when ready
  useEffect(() => {
    if (ui !== "ready") return;
    if (focusedOnceRef.current) return;

    const t = window.setTimeout(() => {
      textareaRef.current?.focus();
      focusedOnceRef.current = true;
    }, 60);

    return () => window.clearTimeout(t);
  }, [ui]);

  async function onCopyLink() {
    setError(null);
    setCopyOk(null);

    try {
      await navigator.clipboard.writeText(replyUrl);
      setCopyOk("Link copied to clipboard.");
      window.setTimeout(() => setCopyOk(null), 2500);
    } catch {
      try {
        window.prompt("Copy this link:", replyUrl);
      } catch {
        setError("Could not copy the link. Please copy it manually.");
      }
    }
  }

  async function onSend() {
    setError(null);
    setCopyOk(null);

    if (cooldownSec > 0) {
      setError(`Please wait ${cooldownSec}s and try again.`);
      return;
    }

    const trimmed = message.trim();
    if (!trimmed) {
      setError("Please type a message.");
      return;
    }
    if (trimmed.length > 2000) {
      setError("Message is too long (max 2000 characters).");
      return;
    }
    if (!token) {
      setError("Missing reply token.");
      return;
    }

    setUi("sending");

    try {
      const resp = await fetch(apiUrl("/api/reply/send"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ token, message: trimmed }),
      });

      if (resp.status === 429) {
        const retry = parseRetryAfterSeconds(resp) ?? 30;
        setCooldownSec(retry);
        setUi("ready");
        setError(`Too many sends. Try again in ${retry}s.`);
        return;
      }

      if (!resp.ok) {
        let detail = "Failed to send your reply.";
        try {
          const pd = await resp.json();
          detail = pd?.detail || pd?.title || detail;
        } catch {
          // ignore
        }
        setUi("ready");
        setError(detail);
        return;
      }

      setUi("sent");
    } catch (e) {
      console.error("Send failed:", e);
      setUi("ready");
      setError("Failed to send your reply. Please try again.");
    }
  }

  const cardClass = `${styles.card} ${mounted ? styles.cardEnter : ""}`;

  return (
    <div className={styles.page}>
      <div className={cardClass}>
        <div className={styles.header}>
          <div>
            <div className={styles.brand}>BootLoop</div>
            <div className={styles.subBrand}>Secure email relay</div>
          </div>

          <button type="button" className={styles.ghostBtn} onClick={onCopyLink}>
            Copy reply link
          </button>
        </div>

        {ui === "loading" && (
          <div className={`${styles.body} ${styles.fadeIn}`}>
            <div className={styles.loadingRow}>
              <div className={styles.spinner} aria-label="Loading" />
              <div>
                <div className={styles.h1}>Loading…</div>
                <div className={styles.p}>Validating your link.</div>
              </div>
            </div>
          </div>
        )}

        {ui === "invalid" && (
          <div className={`${styles.body} ${styles.fadeIn}`}>
            <div className={styles.h1}>Link not valid</div>
            <div className={styles.p}>{error ?? "This link is invalid or has expired."}</div>

            {cooldownSec > 0 && (
              <div className={styles.notice}>
                Try again in <strong>{cooldownSec}s</strong>.
              </div>
            )}

            <div className={styles.infoBox}>
              <div className={styles.infoTitle}>What you can do</div>
              <ul className={styles.ul}>
                <li>Check you opened the latest email.</li>
                <li>Request a new reply link from BootLoop.</li>
              </ul>
            </div>

            {copyOk && <div className={styles.successBox}>{copyOk}</div>}
          </div>
        )}

        {(ui === "ready" || ui === "sending") && (
          <div className={`${styles.body} ${styles.fadeIn}`}>
            <div className={styles.h1}>Reply about your swap interest</div>

            <div className={styles.context}>
              <div className={styles.contextLabel}>Listing</div>
              <div className={styles.contextValue}>
                {ctx?.listingSummary ?? "Listing details unavailable"}
              </div>

              {replyingToText && (
                <div className={styles.contextMeta}>
                  You’re replying to <strong>{replyingToText}</strong>
                </div>
              )}

              {expiryText && (
                <div className={styles.contextMeta}>
                  This link expires: <strong>{expiryText}</strong>
                </div>
              )}
            </div>

            {/* ✅ Recent messages preview */}
            {recent.length > 0 && (
              <div className={styles.previewBox}>
                <div className={styles.previewTitle}>Recent messages</div>

                {recent.map((m, i) => (
                  <div key={i} className={styles.previewItem}>
                    <div className={styles.previewMeta}>
                      <strong>{m.from}</strong> • {new Date(m.createdUtc).toLocaleString()}
                    </div>
                    <div className={styles.previewText}>{m.message}</div>
                  </div>
                ))}
              </div>
            )}

            <label className={styles.label}>
              Your message
              <textarea
                ref={textareaRef}
                className={styles.textarea}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={2000}
                placeholder="Type your message…"
                disabled={ui === "sending" || cooldownSec > 0}
              />
            </label>

            <div className={styles.row}>
              <div className={styles.muted}>
                {remainingChars} characters remaining
                {cooldownSec > 0 && (
                  <>
                    {" "}
                    • <strong>Try again in {cooldownSec}s</strong>
                  </>
                )}
              </div>

              <button
                type="button"
                className={styles.primaryBtn}
                onClick={onSend}
                disabled={ui === "sending" || cooldownSec > 0}
              >
                {ui === "sending" ? "Sending…" : "Send reply"}
              </button>
            </div>

            {error && <div className={styles.errorBox}>{error}</div>}
            {copyOk && <div className={styles.successBox}>{copyOk}</div>}

            <div className={styles.footerNote}>
              For safety, we hide email addresses. Replies are relayed via BootLoop.
            </div>
          </div>
        )}

        {ui === "sent" && (
          <div className={`${styles.body} ${styles.fadeIn}`}>
            <div className={styles.h1}>Reply sent ✅</div>
            <div className={styles.p}>
              Your message has been sent. If they reply, you’ll receive another email with a new link.
            </div>

            <div className={styles.context}>
              <div className={styles.contextLabel}>Listing</div>
              <div className={styles.contextValue}>
                {ctx?.listingSummary ?? "Listing details unavailable"}
              </div>
            </div>

            <div className={styles.actions}>
              <button type="button" className={styles.primaryBtn} onClick={() => window.close()}>
                Close this tab
              </button>
              <button type="button" className={styles.ghostBtn} onClick={onCopyLink}>
                Copy reply link
              </button>
            </div>

            {copyOk && <div className={styles.successBox}>{copyOk}</div>}
          </div>
        )}
      </div>
    </div>
  );
}